import { ErrorMessage } from '@hookform/error-message';
import ReactButton from 'components/Buttons/ReactButton';
import { ErrorFormMessage } from 'components/ErrorFormMessage';
import FlexSelect from 'components/Inputs/FlexSelect';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Col, Form, Input, Row } from 'reactstrap';
import AddressService from '../../../../../../../services/addressService';

import { brazilianStates } from '../../../../../../../constants/brazil_states';

const EditAddressForm = ({
  accountId,
  onAddressUpdated,
  onCancel,
  account,
  address
}) => {
  const { control, errors, setValue, handleSubmit, reset } = useForm();
  const [withoutNumber, setWithoutNumber] = React.useState(false);

  useEffect(() => {
    if (address) {
      setValue('title', address.title || '');
      setValue('zipCode', address.zipCode || '');
      setValue('state', address.state || '');
      setValue('city', address.city || '');
      setValue('neighborhood', address.neighborhood || '');
      setValue('street', address.street || '');
      setValue('complement', address.complement || '');

      const hasNoNumber = !address.number;
      setWithoutNumber(hasNoNumber);
      if (!hasNoNumber) setValue('number', address.number?.toString() || '');
    }
  }, [address, setValue]);

  const onSubmit = async (data) => {
    try {
      const addressData = {
        ...data,
        zipCode: data.zipCode,
        number: withoutNumber ? null : Number(data.number) || null
      };

      await AddressService.updateAddress(address.id, addressData);

      toast.success('Endereço atualizado com sucesso');
      reset();
      onAddressUpdated();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Erro ao atualizar endereço'
      );
    }
  };

  return (
    <Form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col className="mb-3" sm="12">
          <label className="form-control-label text-capitalize">Título*</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Ex: Casa, Trabalho, Endereço principal..."
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="title"
          />
          <ErrorMessage
            errors={errors}
            name="title"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>
      </Row>

      <Row>
        <Col className="mb-3" sm="4">
          <label className="form-control-label text-capitalize">CEP*</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="00000-000"
                maxLength={8}
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="zipCode"
          />
          <ErrorMessage
            errors={errors}
            name="zipCode"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>

        <Col className="mb-3" sm="4">
          <label className="form-control-label text-capitalize">Estado*</label>
          <Controller
            render={(props) => (
              <FlexSelect
                value={props.value}
                onChange={props.onChange}
                placeholder="Selecione um estado"
                options={brazilianStates}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.abbreviation}
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="state"
          />
          <ErrorMessage
            errors={errors}
            name="state"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>

        <Col className="mb-3" sm="4">
          <label className="form-control-label text-capitalize">Cidade*</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Ex: São Paulo"
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="city"
          />
          <ErrorMessage
            errors={errors}
            name="city"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>
      </Row>

      <Row>
        <Col className="mb-3" sm="6">
          <label className="form-control-label text-capitalize">Bairro</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Ex: Centro"
              />
            )}
            control={control}
            defaultValue=""
            name="neighborhood"
          />
        </Col>

        <Col className="mb-3" sm="6">
          <label className="form-control-label text-capitalize">Rua*</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Ex: Rua das Flores"
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="street"
          />
          <ErrorMessage
            errors={errors}
            name="street"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>
      </Row>

      <Row>
        <Col className="mb-3" sm="6">
          <label className="form-control-label text-capitalize">Número</label>
          <div className="d-flex align-items-center mb-2">
            <input
              type="checkbox"
              id="withoutNumber"
              className="mr-2"
              checked={withoutNumber}
              onChange={(e) => {
                const checked = e.target.checked;
                setWithoutNumber(checked);
                if (checked) {
                  setValue('number', '');
                }
              }}
            />
            <label htmlFor="withoutNumber" className="mb-0 ml-1">
              S/N (Sem Número)
            </label>
          </div>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder={withoutNumber ? 'S/N' : 'Ex: 123'}
                disabled={withoutNumber}
                style={{ backgroundColor: withoutNumber ? '#f8f9fa' : 'white' }}
              />
            )}
            control={control}
            defaultValue=""
            name="number"
          />
        </Col>

        <Col className="mb-3" sm="6">
          <label className="form-control-label text-capitalize">
            Complemento
          </label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Ex: Apto 45, Bloco B"
              />
            )}
            control={control}
            defaultValue=""
            name="complement"
          />
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <div className="d-flex justify-content-end">
            <ReactButton
              className="mr-2"
              color="secondary"
              type="button"
              onClick={onCancel}
            >
              Cancelar
            </ReactButton>
            <ReactButton color="primary" type="submit">
              Atualizar Endereço
            </ReactButton>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default EditAddressForm;
